import { memo, useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';

import { cloneDeep } from 'lodash-es';

import { spBookObj } from '@/components/ProductList/overseasList';
import SvgIcon from '@/components/SvgIcon';

const scrollMt = 10; // 留一点高度更好展示

const AnchorNav = forwardRef((props, ref) => {
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
  const { totalCount, providerList } = props;
  const [activeId, setActiveId] = useState(totalCount > 0 ? 'lcxh' : providerList?.[0].provider);

  const isUserAction = useRef(false);
  const elementRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);

  useEffect(() => {
    const updateHeight = () => {
      if (elementRef.current) {
        const newHeight = elementRef.current.scrollHeight;
        setHeight(newHeight);
        setShowToggle(newHeight > 48);
        if (!isUserAction.current) {
          setIsExpanded(false);
        }
      }
    };

    const observer = new ResizeObserver(updateHeight);
    if (elementRef.current) observer.observe(elementRef.current);
    updateHeight();
    return () => observer.disconnect();
  }, [providerList, totalCount]);

  useEffect(() => {
    setIsProgrammaticScroll(true);
    props?.changeMixSortDisabled(activeId === 'lcxh' || activeId === '');
    setTimeout(() => {
      setIsProgrammaticScroll(false);
    }, 1000);
  }, [activeId]);

  const changeActiveId = (provider) => {
    setActiveId(provider);
    setIsProgrammaticScroll(true);
    const anchorHeaderDomHeight = document.querySelector('.anchor-header')?.getBoundingClientRect()?.height;
    const dom = document.querySelector(`#${provider}`);
    window.scrollTo({
      top: dom?.getBoundingClientRect()?.top + window.scrollY - 72 - parseInt(anchorHeaderDomHeight) - scrollMt,
    });
    props?.addAnchorHeaderCls();
    setTimeout(() => {
      setIsProgrammaticScroll(false);
    }, 1000);
  };

  const toTop = (provider) => {
    setActiveId(provider);
    setIsProgrammaticScroll(true);
    props?.addAnchorHeaderCls();
    setTimeout(() => {
      setIsProgrammaticScroll(false);
    }, 1000);
  };

  const setAnchorNavPosition = () => {
    if (isProgrammaticScroll) return;
    const scrollY = window.scrollY;
    const anchorHeaderDomHeight = document.querySelector('.anchor-header')?.getBoundingClientRect()?.height;
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    const offset = 30; // 距底部 30px 时触发最后一个高亮
    const heightArr = [];
    const providerListState = cloneDeep(providerList);
    if (totalCount > 0) {
      providerListState.unshift({
        provider: 'lcxh',
      });
    }
    providerListState.forEach((item) => {
      const dom = document.querySelector(`#${item.provider}`);
      if (dom) {
        heightArr.push({
          provider: item.provider,
          height: dom?.getBoundingClientRect()?.top + scrollY - 72 - parseInt(anchorHeaderDomHeight) - scrollMt,
        });
      }
    });
    let activeIdState = activeId;
    if (scrollY === 0) {
      activeIdState = totalCount > 0 ? 'lcxh' : providerList?.[0].provider;
    } else if (scrollTop + clientHeight >= scrollHeight - offset) {
      activeIdState = providerList.length > 0 ? providerList[providerList.length - 1].provider : '';
    } else {
      heightArr.some((item, index) => {
        if (
          scrollY >= item.height &&
          (!heightArr[index + 1] || (heightArr[index + 1] && scrollY < heightArr[index + 1]?.height))
        ) {
          activeIdState = item.provider;
          return true;
        }
        return false;
      });
    }
    setActiveId(activeIdState || providerListState?.[providerListState?.length - 1]?.provider);
  };

  useImperativeHandle(ref, () => ({
    setAnchorNavPosition,
    toTop,
  }));

  const getShowLabel = (item) => {
    let text;
    if (spBookObj[item.provider]) {
      text = spBookObj[item.provider];
    } else if (item.isLcscBookShow) {
      text = '立创订货';
    } else {
      text = item.providerAlias || item.provider;
    }
    return text;
  };

  const handleToggle = () => {
    isUserAction.current = true;
    setIsExpanded((prev) => !prev);
  };

  const [anchorHoverStatus, setAnchorHoverStatus] = useState(false);
  const AnchorIcon = useMemo(() => {
    if (anchorHoverStatus || isExpanded) {
      return 'navigate-arrow3-active';
    }
    return 'navigate-arrow2';
  }, [isExpanded, anchorHoverStatus]);

  return (
    <div className={'m-auto flex w-full px-[40px] pt-[10px]'}>
      <div className={'z-[1] flex-1'}>
        <ul
          className={`flex flex-wrap justify-start  overflow-hidden`}
          ref={elementRef}
          style={{
            maxHeight: isExpanded ? `${height}px` : '48px',
          }}
        >
          {totalCount > 0 && (
            <li
              className={`mb-[10px] mr-[15px] flex h-[38px] shrink-0 cursor-pointer items-center justify-center px-[14px] text-[14px] hover:text-[#0093E6] ${activeId === 'lcxh' ? 'bg-[#0093E6] rounded-[8px] font-bold text-[#fff] hover:text-[#fff]' : 'font-semibold text-[#444444]'}`}
              onClick={() => changeActiveId('lcxh')}
            >
              立创现货
            </li>
          )}
          {providerList.map((item, index) => {
            const id = item.provider;
            return (
              <li
                key={index}
                className={`mb-[10px] mr-[15px] flex h-[38px] shrink-0 cursor-pointer items-center justify-center px-[14px] text-[14px] hover:text-[#0093E6] ${activeId === id ? 'bg-[#0093E6] rounded-[8px] font-bold text-[#fff] hover:text-[#fff]' : 'font-semibold text-[#444444]'}`}
                onClick={() => changeActiveId(id)}
              >
                {getShowLabel(item)}
              </li>
            );
          })}
        </ul>
      </div>
      {showToggle && (
        <div
          className={'z-[1] ml-[20px] flex h-[38px] w-[25px] items-center'}
          onClick={handleToggle}
          onMouseLeave={() => setAnchorHoverStatus(false)}
          onMouseEnter={() => setAnchorHoverStatus(true)}
        >
          <SvgIcon
            app="so"
            iconName={AnchorIcon}
            className={`size-[25px] cursor-pointer ${!isExpanded ? '' : 'rotate-180 transform'}`}
          ></SvgIcon>
        </div>
      )}
    </div>
  );
});

AnchorNav.displayName = 'AnchorNav';

export default memo(AnchorNav);
